import { Box } from "@mui/material";

import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(4px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

export const TableCard = styled(Box)<{
    $fillHeight?: boolean;
    $flush?: boolean;
}>`
    position: relative;
    background: ${({ $flush }) => ($flush ? "transparent" : "#ffffff")};
    border: ${({ $flush }) =>
        $flush ? "none" : "1px solid rgba(0, 0, 0, 0.05)"};
    border-radius: 10px;
    max-width: 100%;
    min-width: 0;
    margin-bottom: 24px;
    animation: ${fadeIn} 320ms ease;

    overflow: ${({ $fillHeight }) => ($fillHeight ? "hidden" : "visible")};

    ${({ $fillHeight }) =>
        $fillHeight &&
        `
        height: 100%;
        display: flex;
        flex-direction: column;
    `}
`;

// overflow-x is opt-in via $overflowX (table minWidth / fixed columns).
// That also makes this the sticky containing block for vertical th sticky —
// accepted tradeoff when horizontal scroll is required.
export const TableScroll = styled(Box)<{
    $fillHeight?: boolean;
    $overflowX?: boolean;
}>`
    width: 100%;
    max-width: 100%;
    min-width: 0;

    ${({ $overflowX }) =>
        $overflowX &&
        `
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
    `}

    ${({ $fillHeight }) =>
        $fillHeight &&
        `
        flex: 1;
        min-height: 0;
        overflow: auto;
    `}
`;
export const StyledTable = styled.table<{
    $minWidth?: number;
    $fixedLayout?: boolean;
}>`
    width: 100%;
    /* Optional floor only when the caller opts in. A default min-width would
       force page-level horizontal overflow and fight sticky header/footer. */
    ${({ $minWidth }) =>
        $minWidth != null &&
        `
        min-width: ${$minWidth}px;
        width: max(100%, ${$minWidth}px);
    `}
    /* separate is required for position:sticky on th/td — collapse breaks it */
    border-collapse: separate;
    border-spacing: 0;
    font-family: "Nunito", sans-serif;
    ${({ $fixedLayout }) => $fixedLayout && `table-layout: fixed;`}
`;

// Remounted (via content key of visible row ids) whenever the displayed
// data set changes so rows fade/slide in instead of snapping straight in.
export const Tbody = styled.tbody`
    animation: ${fadeIn} 220ms ease;
`;

export const Thead = styled.thead`
    background: #fafafa;
`;

export const TableToolbar = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;
    gap: 8px;
    padding: 8px 12px 4px;
    min-height: 44px;
    box-sizing: border-box;
`;

export const ToolbarActions = styled(Box)`
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 8px;
    min-width: 0;
    min-height: 32px;
`;

export const ToolbarEnd = styled(Box)`
    display: flex;
    align-items: center;
    margin-left: auto;
    flex-shrink: 0;
    min-height: 32px;
`;

export const ColumnMenuHeader = styled(Box)`
    padding: 10px 16px 6px;
    font-family: "Nunito", sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #86868b;
`;

export const ColumnMenuList = styled(Box)`
    padding: 0 12px 8px;
    min-width: 200px;
`;

export const Th = styled.th<{
    $align?: string;
    $width?: string;
    $minWidth?: number;
    $fixedWidth?: number;
    $ellipsis?: boolean;
}>`
    position: sticky;
    top: 0;
    z-index: 2;
    background: #fafafa;
    border-bottom: 1px solid #e5e5ea;
    text-align: ${({ $align }) => $align || "left"};
    padding: 14px 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: #86868b;
    white-space: nowrap;
    box-sizing: border-box;
    ${({ $width }) =>
        $width &&
        `
        width: ${$width};
        max-width: ${$width};
    `}
    ${({ $width, $ellipsis }) =>
        $width &&
        $ellipsis !== false &&
        `
        overflow: hidden;
        text-overflow: ellipsis;
    `}
    ${({ $minWidth }) =>
        $minWidth != null &&
        `
        min-width: ${$minWidth}px;
    `}
    ${({ $fixedWidth }) =>
        $fixedWidth != null &&
        `
        width: ${$fixedWidth}px;
        min-width: ${$fixedWidth}px;
        max-width: ${$fixedWidth}px;
    `}
`;

export const TableRow = styled.tr`
    transition: background 150ms ease;

    &:hover {
        background: #fafafa;
    }

    &:hover td {
        background: #fafafa;
    }
`;

export const Td = styled.td<{
    $align?: string;
    $width?: string;
    $minWidth?: number;
    $fixedWidth?: number;
    $ellipsis?: boolean;
}>`
    text-align: ${({ $align }) => $align || "left"};
    padding: 14px 20px;
    vertical-align: middle;
    background: #ffffff;
    border-bottom: 1px solid #f0f0f2;
    min-width: 0;
    box-sizing: border-box;
    ${({ $width }) =>
        $width &&
        `
        width: ${$width};
        max-width: ${$width};
    `}
    ${({ $width, $ellipsis }) =>
        $width &&
        $ellipsis !== false &&
        `
        overflow: hidden;
        text-overflow: ellipsis;
    `}
    ${({ $minWidth }) =>
        $minWidth != null &&
        `
        min-width: ${$minWidth}px;
    `}
    ${({ $fixedWidth }) =>
        $fixedWidth != null &&
        `
        width: ${$fixedWidth}px;
        min-width: ${$fixedWidth}px;
        max-width: ${$fixedWidth}px;
    `}
    ${({ $fixedWidth, $ellipsis }) =>
        $fixedWidth != null &&
        $ellipsis !== false &&
        `
        overflow: hidden;
        text-overflow: ellipsis;
    `}
    ${({ $ellipsis }) =>
        $ellipsis === false &&
        `
        white-space: normal;
        overflow: visible;
        overflow-wrap: anywhere;
        word-break: break-word;
    `}
`;

export const FillerRow = styled.tr`
    border-bottom: none;
    pointer-events: none;

    &:hover {
        background: transparent;
    }
`;

export const EmptyState = styled(Box)<{ $fillHeight?: boolean }>`
    padding: 48px 20px;
    text-align: center;
    font-family: "Nunito", sans-serif;
    font-size: 14px;
    color: #86868b;

    ${({ $fillHeight }) =>
        $fillHeight &&
        `
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
    `}
`;

export const StatusPill = styled(Box)<{ $status?: string }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    box-sizing: border-box;
    font-family: "Nunito", sans-serif;
    font-size: 12px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 20px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background: ${({ $status }) =>
        $status === "attention"
            ? "rgba(211, 47, 47, 0.1)"
            : $status === "healthy"
              ? "rgba(46, 125, 50, 0.1)"
              : "#f2f2f5"};
    color: ${({ $status }) =>
        $status === "attention"
            ? "#d32f2f"
            : $status === "healthy"
              ? "#2e7d32"
              : "#86868b"};
`;

export const StatusDotSmall = styled.span`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
`;

export const ActionsCell = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    flex-shrink: 0;
    white-space: nowrap;
`;

export const CardList = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0;
    animation: ${fadeIn} 220ms ease;
`;

export const RowCard = styled(Box)`
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const CardFields = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const CardField = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

export const CardLabel = styled(Box)`
    font-family: "Nunito", sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #86868b;
`;

export const CardValue = styled(Box)<{ $ellipsis?: boolean }>`
    font-family: "Nunito", sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #3a3a3c;
    min-width: 0;
    overflow-wrap: anywhere;
    ${({ $ellipsis }) =>
        $ellipsis === false &&
        `
        overflow: visible;
        white-space: normal;
        word-break: break-word;
      `}
`;

export const CardActions = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    padding-top: 4px;
    border-top: 1px solid #f0f0f2;
    flex-wrap: wrap;
`;

export const ExpandCell = styled.td`
    width: 44px;
    padding: 14px 0 14px 16px;
    vertical-align: middle;
    background: #ffffff;
    border-bottom: 1px solid #f0f0f2;
    box-sizing: border-box;
`;

export const ExpandToggle = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    background: transparent;
    color: #86868b;
    border-radius: 6px;
    cursor: pointer;
    transition: background 150ms ease;

    &:hover {
        background: #f2f2f5;
    }

    &:disabled {
        visibility: hidden;
        cursor: default;
    }
`;

export const ExpandedRowCell = styled.td`
    padding: 0 20px 16px 0;
    background: #ffffff;
    border-bottom: 1px solid #f0f0f2;
`;

export const ExpandedContent = styled(Box)`
    animation: ${fadeIn} 200ms ease;
`;

export const CardExpandToggle = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 100%;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: #fafafa;
    color: #3a3a3c;
    border-radius: 8px;
    padding: 8px 12px;
    font-family: "Nunito", sans-serif;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: background 150ms ease;

    &:hover {
        background: #f2f2f5;
    }
`;
